import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { ASSETS } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ArrowLeft, Megaphone, TrendingUp, BadgePercent, Crown, Timer } from "lucide-react";

const TICK_MS = 1500;

function cost(base, level, discLvl) {
  return Math.max(1, Math.round(base * Math.pow(1.15, level) * Math.pow(0.99, discLvl)));
}

function UpgradeBtn({ title, desc, level, costNow, can, onClick, icon: Icon, color, testId, footer }) {
  return (
    <button
      data-testid={testId}
      disabled={!can}
      onClick={onClick}
      className={`card-surface p-5 text-left transition-all ${can ? "hover:scale-[1.02]" : "opacity-50 cursor-not-allowed"}`}
      style={{ borderColor: color + "55" }}
    >
      <div className="flex items-center gap-3 mb-2">
        <Icon size={28} style={{ color }} />
        <div>
          <div className="font-heading text-lg" style={{ color }}>{title}</div>
          <div className="text-xs font-mono text-[#A89BCC]">Level {level}</div>
        </div>
      </div>
      <p className="text-xs text-[#A89BCC] mb-3 italic min-h-[2.2em]">"{desc}"</p>
      <div className="flex items-center justify-between font-mono text-sm">
        <div className="flex items-center gap-2">
          <img src={ASSETS.currency.fan} alt="" className="w-5 h-5" />
          <span style={{ color }} className="font-bold">{costNow.toLocaleString()}</span>
        </div>
        {footer && <span className="text-[10px] text-[#A89BCC]">{footer}</span>}
      </div>
    </button>
  );
}

export default function PromoPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [w, setW] = useState(null);
  const [progress, setProgress] = useState(0);
  const [bursts, setBursts] = useState([]);
  const bidRef = useRef(0);

  const load = async () => {
    const r = await api.get(`/wrestlers/${id}`);
    setW(r.data);
  };

  // Enter promo on mount (sets state=promo on server)
  useEffect(() => {
    api.post(`/wrestlers/${id}/promo/enter`).then(r => setW(r.data)).catch(() => load());
  }, [id]);

  // Active ticker — server credits fans every TICK_MS
  useEffect(() => {
    if (!w) return;
    const start = Date.now();
    const id2 = setInterval(() => {
      const elapsed = (Date.now() - start) % TICK_MS;
      setProgress((elapsed / TICK_MS) * 100);
    }, 50);
    const tickId = setInterval(async () => {
      try {
        const r = await api.post(`/wrestlers/${id}/promo/tick`);
        const bid = ++bidRef.current;
        setBursts(prev => [...prev, { id: bid, value: r.data.fans_gain }]);
        setTimeout(() => setBursts(prev => prev.filter(b => b.id !== bid)), 900);
        refresh();
      } catch (e) {}
    }, TICK_MS);
    return () => { clearInterval(id2); clearInterval(tickId); };
  }, [w?.wrestler_id]);

  const upgrade = async (uid) => {
    try {
      const r = await api.post(`/wrestlers/${id}/promo/upgrade`, { upgrade_id: uid });
      setW(r.data);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const prestige = async () => {
    if (!window.confirm("Reset Promo progress for permanent global Fans bonus?")) return;
    try {
      const r = await api.post(`/wrestlers/${id}/promo/prestige`);
      toast.success(`+${r.data.fame_gain}% permanent Fans for ALL wrestlers!`);
      load(); refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (!w) return <div className="p-8 text-center font-heading neon-cyan">LOADING...</div>;

  const baseLvl = w.promo_level_base ?? 0;
  const effLvl = w.promo_level_eff ?? 0;
  const discLvl = w.promo_level_disc ?? 0;
  const passLvl = w.promo_level_passive ?? 0;
  const presLvl = w.promo_level_pres ?? 0;
  const passivePct = w.effective_passive_pct ?? w.promo_passive_pct ?? 25;
  const prestige_amt = w.promo_prestige ?? 0;
  const prestigeCount = w.prestige_count ?? 0;

  const baseCost = cost(10, baseLvl, discLvl);
  const effCost = cost(25, effLvl, discLvl);
  const discCost = cost(50, discLvl, discLvl);
  const passCost = cost(20, passLvl, discLvl);
  const presCost = cost(75, presLvl, discLvl);

  const totalLvls = baseLvl + effLvl + discLvl;
  const baseReq = 10 + 5 * prestigeCount;
  const presReq = Math.max(1, baseReq - presLvl);
  const canPrestige = totalLvls >= presReq;
  const fansGain = canPrestige ? Math.max(1, Math.floor(totalLvls / Math.max(1, presReq))) : 0;

  const activePerTick = Math.max(1, Math.round(w.promo_base * (1 + (w.promo_eff ?? 0) / 100) * (1 + prestige_amt / 100)));
  const passivePerMin = (w.promo_base * (1 + (w.promo_eff ?? 0) / 100) * (1 + prestige_amt / 100) * (passivePct / 100)).toFixed(1);
  const passiveDisplay = (Number.isInteger(passivePct) ? passivePct : passivePct.toFixed(1));

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <button onClick={() => nav(`/wrestler/${id}`)} className="flex items-center gap-2 text-[#A89BCC] hover:text-[#00F0FF] mb-4 font-mono text-sm uppercase">
        <ArrowLeft size={16} /> Back
      </button>
      <h1 className="font-heading text-4xl mb-1">
        <span className="neon-pink">PROMO</span> <span className="neon-cyan">TOUR</span>
      </h1>
      <p className="text-[#A89BCC] font-mono text-sm mb-6">
        {w.name} works the crowd. Fans tick in actively here, plus a slower passive trickle when off-screen.
      </p>

      {/* Active stage */}
      <div className="card-surface p-6 mb-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15"
             style={{ background: "radial-gradient(circle at 50% 30%, #FF007F, transparent 70%)" }} />
        <div className="relative flex flex-col items-center">
          <Megaphone size={64} className="neon-pink mb-3" />
          <div className="font-heading text-2xl neon-pink mb-1">ON THE MIC</div>
          <div className="font-mono text-sm text-[#A89BCC] mb-4 text-center">
            <span className="neon-pink">+{activePerTick}</span> fans active &nbsp;·&nbsp;
            <span className="neon-cyan">+{passivePerMin}</span> fans/min passive
            {prestige_amt > 0 && <span className="ml-2 neon-yellow">[+{prestige_amt}% Fans Global]</span>}
          </div>

          <div className="w-full max-w-md h-4 bg-[#0A051A] rounded-full overflow-hidden border border-[#FF007F]/40">
            <div className="promo-fill" style={{ width: `${progress}%` }} />
          </div>

          {bursts.map(b => (
            <div key={b.id} className="absolute top-10 left-1/2 dmg-number" style={{ color: "#FF007F", fontSize: "1.8rem" }}>
              +{b.value} <span style={{ fontSize: "1rem" }}>Fans</span>
            </div>
          ))}
        </div>
      </div>

      {/* Upgrades grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <UpgradeBtn
          testId="upgrade-base"
          title="LOUDER MIC"
          desc="Hype the crowd louder. +1 Fans per active tick."
          level={baseLvl}
          costNow={baseCost}
          can={user && user.fans >= baseCost}
          onClick={() => upgrade("base")}
          icon={Megaphone}
          color="#FF007F"
        />
        <UpgradeBtn
          testId="upgrade-eff"
          title="SHARPER PROMO"
          desc="Sharper mic work, stronger reactions. +1% efficiency."
          level={effLvl}
          costNow={effCost}
          can={user && user.fans >= effCost}
          onClick={() => upgrade("eff")}
          icon={TrendingUp}
          color="#00F0FF"
        />
        <UpgradeBtn
          testId="upgrade-discount"
          title="BOOKING DEAL"
          desc="Better booking deals backstage. -1% upgrade cost."
          level={discLvl}
          costNow={discCost}
          can={user && user.fans >= discCost}
          onClick={() => upgrade("discount")}
          icon={BadgePercent}
          color="#FFE600"
        />
        <UpgradeBtn
          testId="upgrade-pres-disc"
          title="SHORTCUT TO LEGACY"
          desc="Reduce prestige requirement by 1 upgrade."
          level={presLvl}
          costNow={presCost}
          can={user && user.fans >= presCost && presLvl < baseReq - 1}
          onClick={() => upgrade("pres_disc")}
          icon={Timer}
          color="#FF3399"
          footer={`req: ${presReq}`}
        />
        <button
          data-testid="upgrade-prestige"
          disabled={!canPrestige}
          onClick={prestige}
          title={canPrestige
            ? `Reset for +${fansGain}% permanent global Fans bonus`
            : `Need ${presReq - totalLvls} more upgrade(s)`}
          className={`card-surface p-5 text-left transition-all ${canPrestige ? "hover:scale-[1.02] border-2 cursor-pointer" : "opacity-50 cursor-not-allowed"}`}
          style={canPrestige ? { borderColor: "#9D00FF", boxShadow: "0 0 20px rgba(157,0,255,0.5)" } : {}}
        >
          <div className="flex items-center gap-3 mb-2">
            <Crown size={28} style={{ color: "#9D00FF" }} />
            <div>
              <div className="font-heading text-lg" style={{ color: "#9D00FF" }}>+% FANS TO ALL WRESTLERS</div>
              <div className="text-xs font-mono text-[#A89BCC]">
                Current: +{prestige_amt}% Fans Global · Resets: {prestigeCount}
              </div>
            </div>
          </div>
          <p className="text-xs text-[#A89BCC] mb-3 italic">"Build a legacy that buffs every wrestler on your roster."</p>
          <div className="font-mono text-xs flex items-center justify-between">
            <span>
              <span className="text-[#A89BCC]">Progress: </span>
              <span className={canPrestige ? "neon-green font-bold" : "text-[#A89BCC]"}>{totalLvls} / {presReq}</span>
            </span>
            {canPrestige
              ? <span className="neon-green">Ready: +{fansGain}% Fans</span>
              : <span className="text-[#A89BCC]">Need {presReq - totalLvls} more</span>}
          </div>
        </button>
      </div>
    </div>
  );
}
